// Draws the finish camera's photo: a black-and-white line-scan picture taken through a slit on the finish line,
// like a racecourse photo-finish camera. Time runs left to right, so the still scenery becomes horizontal streaks
// and each cat appears facing left with the tip of its nose at the moment it crossed. Draws on a 2D canvas context
// it is given; reads nothing from the page.
import { CAT_IDS, type CatId } from '../race/types';
import { CAT_DISPLAY } from './cats';
import { formatNs, mulberry32, type PhotoFinish } from './photoFinish';

/** The stretch of time the photo covers, ns from post time. */
export interface PhotoView { startNs: number; endNs: number }

/** Time shown before the first nose and after the last nose (room for the last cat's body and tail). */
const LEAD_NS = 70e6;
const TRAIL_NS = 240e6;
const MIN_SPAN_NS = 440e6;

export function photoView(p: PhotoFinish): PhotoView {
  const first = p.cats[0]!.ns, last = p.cats[p.cats.length - 1]!.ns;
  let startNs = first - LEAD_NS, endNs = last + TRAIL_NS;
  const short = MIN_SPAN_NS - (endNs - startNs);
  if (short > 0) { startNs -= short * 0.3; endNs += short * 0.7; }
  return { startNs, endNs };
}

export const xAtNs = (v: PhotoView, ns: number, width: number): number => ((ns - v.startNs) / (v.endNs - v.startNs)) * width;
export const nsAtX = (v: PhotoView, x: number, width: number): number => Math.round(v.startNs + (x / width) * (v.endNs - v.startNs));

/** Where things sit, as shares of the picture's height. The furthest lane in view is at the top. */
export const LAYOUT = { laneTop: 0.13, laneBottom: 0.86, scaleTop: 0.885 } as const;

/** The lanes the camera frames: from the nearest cat's lane to one lane beyond the furthest, so the cats fill the picture. */
export interface LaneRange { near: number; far: number }
export function lanesInView(p: PhotoFinish): LaneRange {
  const lanes = p.cats.map((c) => CAT_IDS.indexOf(c.id));
  return { near: Math.min(...lanes), far: Math.min(CAT_IDS.length - 1, Math.max(...lanes) + 1) };
}

/** Top and height of a lane's band in view, nearer lanes taller, as the camera looks across the track. */
export function laneBand(lane: number, h: number, view: LaneRange = { near: 0, far: CAT_IDS.length - 1 }): { top: number; height: number } {
  const weight = (l: number) => 1 + 0.1 * (CAT_IDS.length - 1 - l); // lane 0 nearest
  let total = 0;
  for (let l = view.near; l <= view.far; l++) total += weight(l);
  const span = (LAYOUT.laneBottom - LAYOUT.laneTop) * h;
  let top = LAYOUT.laneTop * h;
  for (let l = view.far; l > lane; l--) top += (weight(l) / total) * span;
  return { top, height: (weight(lane) / total) * span };
}

interface Coat {
  base: number; // grey level 0..255 of the coat as the black-and-white film sees it
  light: number; // white markings
  dark: number; // ear caps, stripes, ticking
  bodyNs: number; // nose to tail tip, in time through the slit
  ears: number; // ear size
  markings: (m: Marker) => void;
}

/** Draws filled ellipses in the cat's own coordinates: u along the body (0 = nose), v down (0 = top, 1 = ground). */
type Marker = (u: number, v: number, ru: number, rv: number, grey: number, alpha?: number) => void;

// The cats as black-and-white film sees them (.taiga/knowledge/ui/index.md): ginger turns mid-grey.
const COATS: Record<CatId, Coat> = {
  nemo: { base: 170, light: 238, dark: 118, bodyNs: 168e6, ears: 1, markings: (m) => {
    for (let i = 0; i < 6; i++) m(0.3 + i * 0.065, 0.3, 0.018, 0.1, 118, 0.55);
    m(0.25, 0.55, 0.1, 0.12, 238); m(0.45, 0.62, 0.14, 0.08, 238); m(0.03, 0.4, 0.04, 0.09, 238);
  } },
  sirius: { base: 236, light: 248, dark: 42, bodyNs: 162e6, ears: 1, markings: (m) => {
    m(0.045, 0.47, 0.016, 0.04, 42); m(0.11, 0.17, 0.05, 0.09, 70, 0.8);
  } },
  tyyne: { base: 30, light: 236, dark: 12, bodyNs: 150e6, ears: 1, markings: (m) => {
    m(0.03, 0.4, 0.045, 0.1, 236); m(0.055, 0.25, 0.009, 0.08, 236); m(0.22, 0.54, 0.08, 0.13, 232); m(0.42, 0.63, 0.1, 0.06, 225, 0.9);
  } },
  silkki: { base: 122, light: 168, dark: 70, bodyNs: 176e6, ears: 1.45, markings: (m) => {
    m(0.45, 0.6, 0.16, 0.07, 168, 0.8); m(0.03, 0.4, 0.035, 0.08, 176);
  } },
  misse: { base: 46, light: 234, dark: 16, bodyNs: 160e6, ears: 1, markings: (m) => {
    m(0.035, 0.4, 0.05, 0.12, 234); m(0.085, 0.38, 0.03, 0.1, 230); m(0.24, 0.55, 0.09, 0.12, 232); m(0.5, 0.6, 0.08, 0.07, 228, 0.9);
  } },
  growl: { base: 198, light: 226, dark: 140, bodyNs: 166e6, ears: 1.35, markings: (m) => {
    m(0.03, 0.4, 0.04, 0.09, 230); m(0.45, 0.6, 0.15, 0.07, 222, 0.8);
  } },
};

const grey = (v: number, a = 1) => {
  const g = Math.round(Math.min(255, Math.max(0, v)));
  return a >= 1 ? `rgb(${g},${g},${g})` : `rgba(${g},${g},${g},${a})`;
};

const MONO = "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace";

/**
 * Draws the photo into a canvas of w × h device pixels. `px` is device pixels per CSS pixel, for line widths
 * and lettering. The same photo always draws the same picture.
 */
export function drawFinishCamera(ctx: CanvasRenderingContext2D, w: number, h: number, px: number, photo: PhotoFinish, raceNo: number): void {
  const view = photoView(photo);
  const rand = mulberry32(photo.cats[0]!.ns % 4294967296);
  const imageBottom = LAYOUT.scaleTop * h;

  const lanes = lanesInView(photo);
  drawScenery(ctx, w, h, imageBottom, lanes, rand);
  // Furthest lane first, so nearer cats cover the ones behind them.
  const order = [...photo.cats].sort((a, b) => CAT_IDS.indexOf(b.id) - CAT_IDS.indexOf(a.id));
  for (const c of order) {
    const lane = CAT_IDS.indexOf(c.id);
    const coat = COATS[c.id];
    const band = laneBand(lane, h, lanes);
    const height = Math.min(band.height * 1.9, 0.42 * h);
    const top = band.top + band.height * 0.92 - height;
    const nose = xAtNs(view, c.ns, w);
    const len = xAtNs(view, c.ns + coat.bodyNs, w) - nose;
    drawCat(ctx, coat, nose, top, len, height, rand);
  }
  filmGrain(ctx, w, Math.ceil(imageBottom), rand);
  drawTimeScale(ctx, w, h, px, view, imageBottom);
  drawCursors(ctx, w, px, view, photo, imageBottom);

  ctx.font = `600 ${9 * px}px ${MONO}`;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.fillStyle = grey(235, 0.75);
  ctx.fillText(`KISSADERBY FINISH CAMERA · RACE ${raceNo} · 10 000 LINES/S`, 6 * px, imageBottom - 4 * px);
}

function drawScenery(ctx: CanvasRenderingContext2D, w: number, h: number, imageBottom: number, lanes: LaneRange, rand: () => number): void {
  // Every row of the slit sees the same spot all the time, so each row is one tone across the whole picture.
  const row = (y0: number, y1: number, tone: (y: number) => number) => {
    for (let y = Math.floor(y0); y < Math.ceil(y1); y++) { ctx.fillStyle = grey(tone(y)); ctx.fillRect(0, y, w, 1); }
  };
  const stand = LAYOUT.laneTop * h;
  // grandstand and crowd beyond the track: busy rows
  row(0, stand * 0.82, (y) => 60 + 90 * rand() * (0.6 + 0.4 * Math.sin(y * 0.7)));
  // far rail: a bright painted rail with a dark shadow
  row(stand * 0.82, stand * 0.9, () => 228);
  row(stand * 0.9, stand, () => 52);
  // lanes: turf rows, a little lighter where the chequered finish stripe's white squares are, timber edging between lanes
  for (let lane = lanes.near; lane <= lanes.far; lane++) {
    const b = laneBand(lane, h, lanes);
    row(b.top, b.top + b.height, (y) => {
      const chequer = Math.floor(((y - b.top) / b.height) * 4) % 2 === 0 ? 14 : -6;
      return 104 + chequer + 22 * (rand() - 0.5);
    });
    row(b.top, b.top + Math.max(1.5, b.height * 0.07), () => 196);
  }
  row(LAYOUT.laneBottom * h, imageBottom, (y) => (y < LAYOUT.laneBottom * h + (imageBottom - LAYOUT.laneBottom * h) * 0.5 ? 222 : 40));
}

function drawCat(ctx: CanvasRenderingContext2D, coat: Coat, x: number, top: number, len: number, hgt: number, rand: () => number): void {
  const X = (u: number) => x + u * len, Y = (v: number) => top + v * hgt;
  const ellipse = (u: number, v: number, ru: number, rv: number) => { ctx.beginPath(); ctx.ellipse(X(u), Y(v), ru * len, rv * hgt, 0, 0, Math.PI * 2); };

  // soft shadow on the turf, carried across the line with the cat
  const shadow = ctx.createRadialGradient(X(0.45), Y(1), 0, X(0.45), Y(1), 0.45 * len);
  shadow.addColorStop(0, 'rgba(0,0,0,0.35)');
  shadow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadow;
  ctx.save();
  ctx.translate(X(0.45), Y(1));
  ctx.scale(1, (0.05 * hgt) / (0.45 * len));
  ctx.translate(-X(0.45), -Y(1));
  ctx.beginPath(); ctx.arc(X(0.45), Y(1), 0.45 * len, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // A tapered limb through joints [u, v], widths in v units, ending in a paw.
  const limb = (pts: [number, number][], widths: number[], tone: number, paw: number) => {
    const left: [number, number][] = [], right: [number, number][] = [];
    pts.forEach(([u, v], i) => {
      const a = pts[Math.max(0, i - 1)]!, b = pts[Math.min(pts.length - 1, i + 1)]!;
      const dx = X(b[0]) - X(a[0]), dy = Y(b[1]) - Y(a[1]);
      const n = Math.hypot(dx, dy) || 1;
      const half = (widths[i]! * hgt) / 2;
      left.push([X(u) - (dy / n) * half, Y(v) + (dx / n) * half]);
      right.push([X(u) + (dy / n) * half, Y(v) - (dx / n) * half]);
    });
    ctx.beginPath();
    left.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
    for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i]![0], right[i]![1]);
    ctx.closePath();
    ctx.fillStyle = grey(tone); ctx.fill();
    const [pu, pv] = pts[pts.length - 1]!;
    ellipse(pu, pv, 0.022, 0.045);
    ctx.fillStyle = grey(paw); ctx.fill();
  };
  const sock = coat.base < 120 || coat.light - coat.base > 40 ? coat.light : coat.base;
  // far legs, in the shade of the body
  limb([[0.25, 0.5], [0.21, 0.72], [0.13, 0.95]], [0.16, 0.08, 0.055], coat.base * 0.7, sock * 0.75);
  limb([[0.6, 0.44], [0.68, 0.66], [0.77, 0.83], [0.85, 0.96]], [0.26, 0.11, 0.065, 0.055], coat.base * 0.7, sock * 0.75);

  // body, neck, head and tail as one outline
  const body = new Path2D();
  const P = (u: number, v: number): [number, number] => [X(u), Y(v)];
  body.moveTo(...P(0.17, 0.42));
  body.bezierCurveTo(...P(0.17, 0.3), ...P(0.24, 0.25), ...P(0.33, 0.27));
  body.bezierCurveTo(...P(0.45, 0.22), ...P(0.6, 0.24), ...P(0.68, 0.32));
  body.bezierCurveTo(...P(0.74, 0.38), ...P(0.73, 0.52), ...P(0.65, 0.56));
  body.bezierCurveTo(...P(0.58, 0.58), ...P(0.5, 0.55), ...P(0.42, 0.6)); // the belly tucks up towards the hind legs
  body.bezierCurveTo(...P(0.34, 0.66), ...P(0.24, 0.66), ...P(0.19, 0.6)); // deep chest
  body.bezierCurveTo(...P(0.15, 0.55), ...P(0.15, 0.48), ...P(0.17, 0.42));
  body.closePath();
  const neck = new Path2D();
  neck.moveTo(...P(0.1, 0.2));
  neck.bezierCurveTo(...P(0.16, 0.16), ...P(0.24, 0.22), ...P(0.27, 0.3));
  neck.lineTo(...P(0.21, 0.56));
  neck.bezierCurveTo(...P(0.15, 0.52), ...P(0.12, 0.46), ...P(0.09, 0.44));
  neck.closePath();
  const head = new Path2D();
  head.ellipse(X(0.09), Y(0.3), 0.062 * len, 0.16 * hgt, 0, 0, Math.PI * 2);
  head.moveTo(X(0.036) + 0.036 * len, Y(0.37));
  head.ellipse(X(0.036), Y(0.37), 0.036 * len, 0.085 * hgt, 0, 0, Math.PI * 2); // muzzle: its tip at u = 0 is the nose
  const tail = new Path2D();
  tail.moveTo(...P(0.66, 0.3));
  tail.bezierCurveTo(...P(0.78, 0.16), ...P(0.88, 0.1), ...P(1, 0.2));
  tail.bezierCurveTo(...P(0.89, 0.16), ...P(0.8, 0.24), ...P(0.69, 0.4));
  tail.closePath();
  // ears: the far one darker, behind the head
  const ear = (u0: number, u1: number, tip: [number, number], tone: number) => {
    const s = coat.ears;
    ctx.beginPath();
    ctx.moveTo(...P(u0, 0.19)); ctx.lineTo(...P(tip[0], 0.19 - (0.19 - tip[1]) * s)); ctx.lineTo(...P(u1, 0.2)); ctx.closePath();
    ctx.fillStyle = grey(tone); ctx.fill();
  };
  ear(0.105, 0.15, [0.135, 0.03], coat.base * 0.75);

  const silhouette = new Path2D();
  silhouette.addPath(body); silhouette.addPath(neck); silhouette.addPath(head); silhouette.addPath(tail);
  ctx.fillStyle = grey(coat.base);
  ctx.fill(body); ctx.fill(neck); ctx.fill(head); ctx.fill(tail);

  ctx.save();
  ctx.clip(silhouette, 'nonzero');
  // markings
  coat.markings((u, v, ru, rv, g, a = 1) => { ellipse(u, v, ru, rv); ctx.fillStyle = grey(g, a); ctx.fill(); });
  // fur: short strokes along the body, light on the back and dark on the belly
  const strokes = Math.round((len * hgt) / 18);
  ctx.lineWidth = Math.max(1, hgt / 110);
  for (let i = 0; i < strokes; i++) {
    const u = rand() * 1.02, v = 0.12 + rand() * 0.55;
    const d = (rand() - 0.5) * 50 + (coat.base < 90 ? 20 : 0);
    ctx.strokeStyle = grey(coat.base + d, 0.35);
    ctx.beginPath(); ctx.moveTo(X(u), Y(v)); ctx.lineTo(X(u + 0.012 + rand() * 0.02), Y(v + (rand() - 0.3) * 0.04)); ctx.stroke();
  }
  if (coat.base > 90 && coat.base < 210) { // ticked coats
    for (let i = 0; i < strokes / 2; i++) { ctx.fillStyle = grey(rand() < 0.5 ? coat.dark : coat.light, 0.4); ctx.fillRect(X(rand()), Y(0.15 + rand() * 0.5), 1.2, 1.2); }
  }
  // light from above: lit back, shaded belly
  const shade = ctx.createLinearGradient(0, Y(0.1), 0, Y(0.65));
  shade.addColorStop(0, 'rgba(255,255,255,0.22)');
  shade.addColorStop(0.45, 'rgba(255,255,255,0)');
  shade.addColorStop(1, 'rgba(0,0,0,0.38)');
  ctx.fillStyle = shade;
  ctx.fillRect(X(-0.02), Y(0), len * 1.05, hgt * 0.7);
  ctx.restore();

  // near ear, eye, nose, whiskers
  ear(0.07, 0.115, [0.085, 0.0], coat === COATS.sirius ? coat.dark : coat.base);
  ellipse(0.062, 0.27, 0.011, 0.04); ctx.fillStyle = grey(coat.base < 90 ? 205 : 190); ctx.fill();
  ellipse(0.063, 0.27, 0.004, 0.034); ctx.fillStyle = grey(8); ctx.fill();
  ellipse(0.005, 0.34, 0.006, 0.022); ctx.fillStyle = grey(coat.base < 90 ? 70 : 35); ctx.fill();
  ctx.strokeStyle = grey(coat.base < 120 ? 225 : 30, 0.55);
  ctx.lineWidth = Math.max(0.8, hgt / 160);
  for (const [du, dv] of [[0.05, 0.05], [0.055, 0.1], [0.05, 0.14]] as const) {
    ctx.beginPath(); ctx.moveTo(X(0.03), Y(0.4)); ctx.lineTo(X(0.03 + du), Y(0.4 + dv)); ctx.stroke();
  }
  // near legs over the body
  limb([[0.23, 0.48], [0.18, 0.68], [0.07, 0.86]], [0.18, 0.085, 0.06], coat.base * 0.95, sock);
  limb([[0.63, 0.42], [0.72, 0.62], [0.8, 0.78], [0.9, 0.87]], [0.28, 0.12, 0.07, 0.06], coat.base * 0.95, sock);
  // a faint edge so pale cats read against the turf
  if (coat.base > 180) {
    ctx.strokeStyle = grey(0, 0.18);
    ctx.lineWidth = Math.max(1, hgt / 160);
    ctx.stroke(silhouette);
  }
}

/** Film grain, a slight flicker between scan lines (columns) and a vignette. Keeps the picture grey. */
function filmGrain(ctx: CanvasRenderingContext2D, w: number, h: number, rand: () => number): void {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const gain = Array.from({ length: w }, () => 1 + (rand() - 0.5) * 0.05);
  for (let y = 0; y < h; y++) {
    const vy = (y / h - 0.5) * 2;
    for (let x = 0; x < w; x++) {
      const vx = (x / w - 0.5) * 2;
      const vignette = 1 - 0.18 * (vx * vx * 0.6 + vy * vy);
      const i = (y * w + x) * 4;
      const g = (d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114) * gain[x]! * vignette + (rand() + rand() - 1) * 14;
      d[i] = d[i + 1] = d[i + 2] = g;
    }
  }
  ctx.putImageData(img, 0, 0);
}

/** Tick spacing in ms so minor ticks are at least `minPx` apart. */
export function tickStepMs(view: PhotoView, w: number, minPx: number): number {
  const pxPerMs = w / ((view.endNs - view.startNs) / 1e6);
  return [1, 2, 5, 10, 20, 50, 100].find((s) => s * pxPerMs >= minPx) ?? 100;
}

/** Labelled ticks: a round number of ms at least four minor ticks apart. */
export function majorStepMs(minor: number): number {
  return [10, 20, 50, 100, 200, 500].find((m) => m >= minor * 4 && m % minor === 0) ?? minor * 5;
}

function drawTimeScale(ctx: CanvasRenderingContext2D, w: number, h: number, px: number, view: PhotoView, top: number): void {
  ctx.fillStyle = grey(14);
  ctx.fillRect(0, top, w, h - top);
  const minor = tickStepMs(view, w, 7 * px);
  const major = majorStepMs(minor);
  const firstMs = Math.ceil(view.startNs / 1e6 / minor) * minor;
  ctx.font = `600 ${9.5 * px}px ${MONO}`;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'center';
  let lastLabelEnd = -Infinity;
  for (let ms = firstMs; ms * 1e6 <= view.endNs; ms += minor) {
    const x = Math.round(xAtNs(view, ms * 1e6, w)) + 0.5;
    const isMajor = ms % major === 0;
    ctx.fillStyle = grey(230, isMajor ? 1 : 0.6);
    ctx.fillRect(x - 0.5 * px, top, px, (isMajor ? 7 : 3.5) * px);
    if (isMajor) {
      ctx.fillStyle = grey(255, 0.1);
      ctx.fillRect(x - 0.5 * px, 0, px, top); // faint grid line over the picture
      const s = ms / 1000;
      const m = Math.floor(s / 60);
      const label = `${m}:${(s - m * 60).toFixed(major < 100 ? 2 : 1).padStart(major < 100 ? 5 : 4, '0')}`;
      const half = ctx.measureText(label).width / 2;
      if (x - half > lastLabelEnd + 6 * px && x - half > 0 && x + half < w) {
        ctx.fillStyle = grey(230);
        ctx.fillText(label, x, h - 2 * px);
        lastLabelEnd = x + half;
      }
    }
  }
}

/** A cursor line through each cat's nose, labelled with its place and name at the top. */
function drawCursors(ctx: CanvasRenderingContext2D, w: number, px: number, view: PhotoView, photo: PhotoFinish, bottom: number): void {
  ctx.font = `700 ${10 * px}px ${MONO}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  const rowH = 15 * px;
  photo.cats.forEach((c, i) => {
    const x = Math.round(xAtNs(view, c.ns, w));
    ctx.fillStyle = grey(0, 0.7);
    ctx.fillRect(x - 1.5 * px, 0, 3 * px, bottom);
    ctx.fillStyle = grey(255);
    ctx.fillRect(x - 0.5 * px, 0, px, bottom);
    const label = `${c.place}  ${CAT_DISPLAY[c.id].short.toUpperCase()}  ${formatNs(c.ns)}`;
    const tw = ctx.measureText(label).width + 10 * px;
    const y = 4 * px + i * (rowH + 2 * px);
    const left = x + 4 * px + tw <= w ? x + 4 * px : x - 4 * px - tw;
    ctx.fillStyle = grey(0, 0.78);
    ctx.fillRect(left, y, tw, rowH);
    ctx.fillStyle = grey(255);
    ctx.fillText(label, left + 5 * px, y + rowH / 2 + 0.5 * px);
  });
}
